import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Input, Button, Spin } from 'antd';
import { PaperPlaneTilt } from '@phosphor-icons/react';
import useReferralMessages from '../../hooks/useReferralMessages';
import useSingleFlight from '../../hooks/useSingleFlight';
import './referralMessages.scss';

const { TextArea } = Input;

const ReferralMessages = ({ slug, archived, cable, user, loggedIn, onUnread }) => {
  const [messageContent, setMessageContent] = useState('');
  const {
    messages,
    sendMessage,
    markAsRead,
    loading,
    referrerOrg,
    partnerOrg,
    showStartMessage,
    hasUnread,
    setActive,
    messagesEndRef,
    sendError,
    shareRevoked,
  } = useReferralMessages(slug, cable, user, loggedIn);

  useEffect(() => {
    setActive(true);
    return () => setActive(false);
  }, [setActive]);

  // Notify parent of unread state changes
  useEffect(() => {
    if (onUnread) onUnread(hasUnread);
  }, [hasUnread, onUnread]);

  // Mark last message as read when messages tab is active
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (lastMsg.message_id && lastMsg.sender_email !== user?.uid) {
        markAsRead(lastMsg.message_id);
      }
    }
  }, [messages, markAsRead, user]);

  const inputDisabled = archived || shareRevoked;

  // Single-flight guard for same-task cable sends.
  const guardedSend = useSingleFlight((content) => sendMessage(content));

  const handleSend = () => {
    if (!messageContent.trim() || inputDisabled) return;
    guardedSend(messageContent);
    setMessageContent('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTimestamp = (timestamp, type) => {
    const dateTime = new Date(timestamp);
    if (type === 'date') {
      return dateTime.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    }
    return dateTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const isCurrentUser = (msg) => msg.sender_email === user?.uid;

  const renderMessage = (msg, index) => {
    const isSender = isCurrentUser(msg);
    const showDate = index === 0 ||
      formatTimestamp(msg.timestamp, 'date') !== formatTimestamp(messages[index - 1].timestamp, 'date');
    const showName = index === 0 || msg.sender_name !== messages[index - 1].sender_name;

    return (
      <div key={msg.message_id || index}>
        {showDate && (
          <div className="referral-messages__datestamp">
            {formatTimestamp(msg.timestamp, 'date')}
          </div>
        )}
        {showName && (
          <div className={`referral-messages__sender-name ${isSender ? 'referral-messages__sender-name--right' : ''}`}>
            {msg.sender_name}
          </div>
        )}
        <div className={`referral-messages__bubble ${isSender ? 'referral-messages__bubble--sent' : 'referral-messages__bubble--received'}`}>
          <p className="referral-messages__bubble-text">{msg.content}</p>
          <span className="referral-messages__bubble-time">
            {formatTimestamp(msg.timestamp, 'time')}
          </span>
        </div>
        {msg.last_read_by && index === messages.length - 1 && isSender && (
          <div className="referral-messages__read-receipt">
            Last viewed by {msg.last_read_by}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="referral-messages__loading">
        <Spin size="large" />
      </div>
    );
  }

  const otherOrg = user?.organization === referrerOrg ? partnerOrg : referrerOrg;

  return (
    <div className="referral-messages">
      <div className={`referral-messages__list ${showStartMessage ? 'referral-messages__list--empty' : ''}`}>
        {showStartMessage ? (
          <div className="referral-messages__start">
            <p>Start a conversation{otherOrg ? ` with ${otherOrg}` : ''}.</p>
          </div>
        ) : (
          messages.map(renderMessage)
        )}
        <div ref={messagesEndRef} />
      </div>
      {sendError && (
        <div className="referral-messages__send-error">{sendError}</div>
      )}
      <div className="referral-messages__input-area">
        {inputDisabled ? (
          <div className="referral-messages__archived-notice">
            {shareRevoked
              ? 'Share has been revoked. Messaging is disabled.'
              : 'This referral is archived. Messaging is disabled.'}
          </div>
        ) : (
          <>
            <TextArea
              placeholder="Write a message..."
              value={messageContent}
              onChange={(e) => setMessageContent(e.target.value)}
              onKeyDown={handleKeyDown}
              autoSize={{ minRows: 1, maxRows: 4 }}
              className="referral-messages__textarea"
            />
            <Button
              type="primary"
              onClick={handleSend}
              disabled={!messageContent.trim()}
              className="referral-messages__send-btn"
              icon={<PaperPlaneTilt size={18} weight="fill" />}
            />
          </>
        )}
      </div>
    </div>
  );
};

ReferralMessages.propTypes = {
  slug: PropTypes.string,
  archived: PropTypes.bool,
  cable: PropTypes.object,
  user: PropTypes.object,
  loggedIn: PropTypes.bool,
  onUnread: PropTypes.func,
};

ReferralMessages.defaultProps = {
  slug: null,
  archived: false,
  cable: null,
  user: null,
  loggedIn: false,
  onUnread: null,
};

export default ReferralMessages;
